import Backbone from 'backbone';
import _ from 'underscore';
import ko from 'knockout';

import {
  formatCost,
  formatBid,
  formatInteger,
  NOT_AVAILABLE_DEFAULT_STR,
  CONVERSION_STATUS_TYPE,
} from '@bingads-webui-campaign/recommendation-core';
import { addRecommendationAlertPopup } from '@bingads-webui-campaign-react/recommendation-alert-popup';
import { triggerContainerResizing } from '@bingads-webui-campaign-react/virtual-scrollbar-recalculate-trigger';

import ViewModel from '../viewmodel/table';
import template from '../template/table.pug';
import templateForInlineEditor from '../template/tableForInlineEditor.pug';

import { COLUMNS_METRICS, defaultColumnMetric } from './const';

const COMMENT_TYPES = {
  SUGGESTED: 'Suggested',
  CURRENT: 'Current',
  MAXVALUE: 'MaxValue',
};
export const KEYSENTER = 13;

export class BudgetLandScapeTable extends Backbone.View {
  initialize({
    opportunityData,
    i18n,
    conversionSummaryStatus,
    appConfig,
    currentBudget = null,
    fromBudgetColumn = false,
    fromCampaignTab = false,
    view = null,
    readOnly,
    isSharedBudget = false,
    // need these configuration for shell ui
    budgetRange = null,
    permissions = {},
  }) {
    this.view = view;
    this.opportunityId = opportunityData.OpportunityId;
    this.campaignId = opportunityData.Target && opportunityData.Target.CampaignId;
    this.opportunityData = opportunityData;
    this.landscapeData = _.filter(opportunityData.Landscapes, ({
      Comment,
    }) => Comment !== COMMENT_TYPES.MAXVALUE);
    this.currentBudget = currentBudget;
    this.fromBudgetColumn = fromBudgetColumn;
    this.fromCampaignTab = fromCampaignTab;
    this.i18n = i18n;
    this.conversionSummaryStatus = conversionSummaryStatus;
    this.isForecasting = opportunityData.IsForecasting;
    this.permissions = permissions;

    if (appConfig) {
      this.minBudget = appConfig.get('BudgetRange').DailyRange.FloorValue;
      this.maxBudget = appConfig.get('BudgetRange').DailyRange.CeilingValue;
    } else if (budgetRange) {
      this.minBudget = budgetRange.FloorValue;
      this.maxBudget = budgetRange.CeilingValue;
    }

    this.config = this.getTableConfig();
    const {
      headers,
      landscapes,
      suggestedIndex,
      currentDataRowIndex,
      defaultColumnIndex,
      defaultRowIndex,
      inputRowIndex,
      defaultBudgetValue,
    } = this.config;

    this.feedbackPersonalizedPopup = null;
    this.isInProductFeedbackLoopOn =
      permissions.IsRecommendationInProductFeedbackLoopEnabled &&
      opportunityData.IsFeedbackPersonalized;

    this.ko_model = new ViewModel({
      rawLandscapeData: this.landscapeData,
      i18n,
      headers,
      landscapes,
      suggestedIndex,
      currentDataRowIndex,
      defaultColumnIndex,
      defaultRowIndex,
      inputRowIndex,
      defaultBudgetValue,
      view: this,
      readOnly,
      isSharedBudget,
      minBudget: this.minBudget,
      maxBudget: this.maxBudget,
    });
    if (!this.fromBudgetColumn) {
      this.ko_model.selectedRowIndex.subscribe(() => {
        this.trigger('selectedRowChanged');
      });
      this.ko_model.selectedColumnIndex.subscribe(() => {
        this.trigger('selectedColumnChanged');
      });
      this.ko_model.estimationKPIArray.subscribe(() => {
        this.trigger('estimationChanged');
      });
    }
  }

  get events() {
    return {
      'click thead th': 'onColumnClicked',
      'click tbody td': 'onColumnClicked',
      'click tbody tr': 'onRowClicked',
      'keydown .user-input-cell input': 'onKeyboardCustomInput',
    };
  }

  isValid() {
    if (this.ko_model.inputRowSelected()) {
      this.ko_model.validate();
      return !this.ko_model.showErrorMessage();
    }
    return true;
  }

  onColumnClicked(e) {
    if (this.fromBudgetColumn) {
      return;
    }
    const { cellIndex } = e.currentTarget;
    // cellIndex === 0 indicates the radio column
    if (cellIndex !== 0) {
      this.ko_model.selectedColumnIndex(e.currentTarget.cellIndex - 1);
    }
  }

  onRowClicked(e) {
    // e.currentTarget.rowIndex is from 1
    this.ko_model.selectedRowIndex(e.currentTarget.rowIndex - 1);
  }

  onKeyboardCustomInput(e) {
    if (e && e.which === KEYSENTER) {
      e.preventDefault();
      e.stopPropagation();
      this.trigger('saveCustomValueWithKeyboard');
    }
  }

  render() {
    if (this.fromBudgetColumn) {
      this.$el.html(templateForInlineEditor({
        i18n: this.i18n,
        currencySymbolClassName: `symbol-size-${this.i18n.getCurrencySymbol().length}`,
        currencySymbol: this.i18n.getCurrencySymbol(),
        notAvailableStr: NOT_AVAILABLE_DEFAULT_STR,
      }));

      if (this.view) {
        this.view.$('.input-budget-container').hide();
        this.view.$('.select-budget-container').attr('class', 'select-budget-container-with-landscape');
      }
    } else {
      this.$el.html(template({
        i18n: this.i18n,
        placeHolderText: this.i18n.getString('BudgetLandscape_Table_CustomInput'),
        currencySymbol: this.i18n.getCurrencySymbol(),
        notAvailableStr: NOT_AVAILABLE_DEFAULT_STR,
      }));
    }
    ko.applyBindings(this.ko_model, this.el);

    if (this.isInProductFeedbackLoopOn) {
      addRecommendationAlertPopup({
        alertMsg: this.i18n.getString('Recommendation_AlertMsg_FeedbackPersonalized'),
        iconClass: 'iconba-Insights',
        position: 'right',
        container: this.$el.find('.tooltip-container').get(0),
      });
    }

    if (this.fromCampaignTab) {
      triggerContainerResizing();
    }
    return this;
  }

  isSuggested() {
    return this.ko_model.selectedRowIndex() === this.config.suggestedIndex;
  }

  getSelectedNewBudget() {
    const selectedRow = this.ko_model.selectedRowIndex();
    if (selectedRow < this.landscapeData.length && selectedRow >= 0) {
      return this.landscapeData[selectedRow][COLUMNS_METRICS.budget];
    }
    return this.ko_model.inputBudget();
  }

  getViewModel() {
    return this.ko_model;
  }

  formatIntegerValue(value) {
    if (value < 1000) {
      return formatInteger(value, this.i18n);
    }
    // this is to reuse the formatBid to always 2 decimals
    return formatBid(value, this.i18n);
  }

  getFormattedTableCell(header, value) {
    let result;

    switch (header) {
      case COLUMNS_METRICS.budget:
        result = this.i18n.formatCurrency(value);
        break;
      case COLUMNS_METRICS.cost:
        result = formatCost(value, this.i18n, this.i18n.currency);
        break;
      case COLUMNS_METRICS.conversion:
        result = this.conversionSummaryStatus === CONVERSION_STATUS_TYPE.HAS_DATA ?
          this.formatIntegerValue(value, this.i18n) : NOT_AVAILABLE_DEFAULT_STR;
        break;
      default:
        result = this.formatIntegerValue(value, this.i18n);
        break;
    }

    return result;
  }

  getTableConfig() {
    const { landscapeData } = this;
    const currentBudget = this.fromCampaignTab ?
      this.currentBudget : this.opportunityData.CurrentBudget;
    let suggestedIndex = -1;
    let currentDataRowIndex = -1;
    let headers = [
      COLUMNS_METRICS.budget,
      COLUMNS_METRICS.impression,
      COLUMNS_METRICS.click,
      COLUMNS_METRICS.conversion,
      COLUMNS_METRICS.cost,
    ];

    if (this.fromBudgetColumn) {
      headers = _.without(headers, COLUMNS_METRICS.impression, COLUMNS_METRICS.cost);

      if (this.conversionSummaryStatus !== CONVERSION_STATUS_TYPE.HAS_DATA) {
        headers = _.without(headers, COLUMNS_METRICS.conversion);
      }
    }

    const defaultColumnIndex = _.findIndex(headers, header => header === defaultColumnMetric);

    const formattedData = _.map(landscapeData, (landscape, index) => {
      const { Comment } = landscape;

      const isSuggested = Comment === COMMENT_TYPES.SUGGESTED;
      if (isSuggested) {
        suggestedIndex = index;
      }
      const isCurrent = landscape.EstimatedBudget === currentBudget;
      if (isCurrent) {
        currentDataRowIndex = index;
      }

      const result = {
        rawBudget: landscape[COLUMNS_METRICS.budget],
        isSuggested,
        isCurrent,
        currentDataRowIndex,
      };
      _.each(headers, (header) => {
        result[header] = this.getFormattedTableCell(header, landscape[header]);
      });
      return result;
    });

    // For inline budget suggestion view:
    // 1. will not show current budget row because current budget showed in input row
    // 2. default selected is input row
    // 3. input field need to be current budget value
    return {
      headers,
      landscapes: formattedData,
      suggestedIndex,
      currentDataRowIndex,
      defaultColumnIndex,
      defaultRowIndex: this.fromBudgetColumn ? formattedData.length : suggestedIndex,
      inputRowIndex: formattedData.length,
      defaultBudgetValue: this.fromBudgetColumn ? currentBudget.toString() : '',
    };
  }
}
